import type { UIMessage } from 'ai';
import { sanitizeAssistantHistory } from '@almanac/contracts';

// Keys are scoped per authenticated user (their OAuth sub) so a shared
// browser profile never surfaces one account's transcripts to another.
function conversationsKey(scope: string): string {
  return `almanac.assistant.v1.${scope}.conversations`;
}
function activeKey(scope: string): string {
  return `almanac.assistant.v1.${scope}.active`;
}

const TITLE_MAX_CHARS = 80;
const UNTITLED = 'Untitled';

export interface StoredConversation {
  id: string;
  title: string;
  researchId?: string;
  messages: UIMessage[];
  createdAt: string; // ISO timestamp
  updatedAt: string; // ISO timestamp
}

/** Working-memory retention window: conversations older than this are dropped. */
export const ASSISTANT_RETENTION_DAYS = 21;
/** Working-memory cap: only the newest conversations (by updatedAt) survive pruning. */
export const ASSISTANT_MAX_CONVERSATIONS = 30;

function isStoredConversation(value: unknown): value is StoredConversation {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.id === 'string' &&
    typeof candidate.title === 'string' &&
    Array.isArray(candidate.messages) &&
    typeof candidate.createdAt === 'string' &&
    typeof candidate.updatedAt === 'string'
  );
}

/** Read and parse a JSON array from localStorage, tolerating missing/corrupt data. */
function readConversationsRaw(scope: string): StoredConversation[] {
  let raw: string | null;
  try {
    raw = localStorage.getItem(conversationsKey(scope));
  } catch {
    return [];
  }
  if (!raw) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];
  return parsed.filter(isStoredConversation);
}

/** Drop stale entries and cap the remainder to the newest ASSISTANT_MAX_CONVERSATIONS. */
function pruneConversations(conversations: StoredConversation[]): StoredConversation[] {
  const cutoff = Date.now() - ASSISTANT_RETENTION_DAYS * 24 * 60 * 60 * 1000;
  const retained = conversations.filter((conversation) => {
    const updatedAt = Date.parse(conversation.updatedAt);
    return !Number.isNaN(updatedAt) && updatedAt >= cutoff;
  });
  retained.sort((a, b) => Date.parse(b.updatedAt) - Date.parse(a.updatedAt));
  return retained.slice(0, ASSISTANT_MAX_CONVERSATIONS);
}

/** Write conversations to localStorage; on quota failure drop the oldest half and retry once. */
function writeConversations(scope: string, conversations: StoredConversation[]): void {
  try {
    localStorage.setItem(conversationsKey(scope), JSON.stringify(conversations));
  } catch {
    const sorted = [...conversations].sort(
      (a, b) => Date.parse(b.updatedAt) - Date.parse(a.updatedAt),
    );
    const trimmed = sorted.slice(0, Math.ceil(sorted.length / 2));
    try {
      localStorage.setItem(conversationsKey(scope), JSON.stringify(trimmed));
    } catch {
      // Give up silently — working memory is best-effort.
    }
  }
}

/** All retained conversations, pruned and sorted by updatedAt descending. */
export function loadConversations(scope: string): StoredConversation[] {
  return pruneConversations(readConversationsRaw(scope));
}

/** One conversation by id, or null. */
export function loadConversation(scope: string, id: string): StoredConversation | null {
  const found = loadConversations(scope).find((conversation) => conversation.id === id);
  return found ?? null;
}

function firstTextPart(message: UIMessage): string | null {
  for (const part of message.parts) {
    if (part.type === 'text' && part.text.trim().length > 0) return part.text;
  }
  return null;
}

/** Derive a short title from the first user message's first text part. */
function deriveTitle(messages: UIMessage[]): string {
  const firstUserMessage = messages.find((message) => message.role === 'user');
  const text = firstUserMessage ? firstTextPart(firstUserMessage) : null;
  if (!text) return UNTITLED;
  const trimmed = text.trim();
  if (!trimmed) return UNTITLED;
  if (trimmed.length <= TITLE_MAX_CHARS) return trimmed;
  return trimmed.slice(0, TITLE_MAX_CHARS);
}

/** Upsert one conversation: sanitizes messages, derives title, stamps timestamps, prunes, quota-guards. */
export function saveConversation(
  scope: string,
  input: {
    id: string;
    researchId?: string;
    messages: UIMessage[];
  },
): void {
  try {
    const sanitized = sanitizeAssistantHistory(input.messages);
    const existing = readConversationsRaw(scope);
    const withoutThis = existing.filter((conversation) => conversation.id !== input.id);

    if (sanitized.length === 0) {
      writeConversations(scope, pruneConversations(withoutThis));
      return;
    }

    const now = new Date().toISOString();
    const previous = existing.find((conversation) => conversation.id === input.id);
    const next: StoredConversation = {
      id: input.id,
      title: deriveTitle(sanitized),
      researchId: input.researchId,
      messages: sanitized,
      createdAt: previous?.createdAt ?? now,
      updatedAt: now,
    };

    writeConversations(scope, pruneConversations([...withoutThis, next]));
  } catch {
    // Working memory is best-effort — never let a persistence failure break chat.
  }
}

export function deleteConversation(scope: string, id: string): void {
  try {
    const remaining = readConversationsRaw(scope).filter((conversation) => conversation.id !== id);
    writeConversations(scope, pruneConversations(remaining));
  } catch {
    // Working memory is best-effort.
  }
}

export function loadActiveConversationId(scope: string): string | null {
  let id: string | null;
  try {
    id = localStorage.getItem(activeKey(scope));
  } catch {
    return null;
  }
  if (!id) return null;
  const retained = loadConversations(scope).some((conversation) => conversation.id === id);
  return retained ? id : null;
}

export function setActiveConversationId(scope: string, id: string | null): void {
  try {
    if (id === null) {
      localStorage.removeItem(activeKey(scope));
    } else {
      localStorage.setItem(activeKey(scope), id);
    }
  } catch {
    // Working memory is best-effort.
  }
}
